import React, { Suspense } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useLocation, Route, Routes } from "react-router-dom";
import { useCookies } from "react-cookie";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getRandomWallpaper, getTranslate, selectTranslate, selectTranslateLanguage, selectTranslateStatus, selectWallpaper } from "@/store/langSlice";
import { getAccessRightsList, getDatasetsList, getVersion, refreshToken, selectAccessRights, selectAuthData, selectTokenAuth } from "@/store/authSlice";
import { clearCurrentUserInfo, getCurrentUserInfo, selectCurrentUserInfo } from "@/store/userSlice";
import {
	QAS_FILTER_ANSWER,
	QAS_FILTER_ANSWER_TYPE,
	QAS_FILTER_CATEGORY,
	QAS_FILTER_COMPLETENESS,
	QAS_FILTER_END_DATE,
	QAS_FILTER_QUESTION,
	QAS_FILTER_START_DATE,
	REFRESH_TOKEN,
} from "@/constants/cookieNames";
import { DATASET, USER } from "@/constants/accessRights";
import { RequestStatus } from "@/types/statusTypes";
import { useAccessRight, useTranslate } from "@/hooks";
import {
	SBS,
	CLS,
	SMC,
	SEE,
	LOGIN,
	QAS_INTERACTIVE,
	QAS_QUESTION,
	QAS,
	SPR,
	RES,
	SES,
	SEE_CORPUS,
	SMC_CORPUS,
	SMC_MARKS,
	SPR_TRANSCRIPT,
	TTS,
	USER_ROLES,
	USERS,
} from "@/constants/routes";
import { NoticeSingleAction, ScreenLock } from "@/components";
import { setFiltersByQas } from "@/store/qas/qasSlice";

// ленивые импорты
const Home = React.lazy(() => import("@/pages/Home/Home"));
const Login = React.lazy(() => import("@/pages/Login/Login"));
const Classifier = React.lazy(() => import("@/pages/Classifier/Classifier"));
const Entities = React.lazy(() => import("@/pages/Entities/Entities"));
const Corpus = React.lazy(() => import("@/pages/Corpus/Corpus"));
const Marks = React.lazy(() => import("@/pages/Marks/Marks"));
const Queue = React.lazy(() => import("@/pages/Queue/Queue"));
const Transcript = React.lazy(() => import("@/pages/Transcript/Transcript"));
const Biometry = React.lazy(() => import("@/pages/Biometry/Biometry"));
const Synthesis = React.lazy(() => import("@/pages/Synthesis/Synthesis"));
const Questionnaire = React.lazy(() => import("@/pages/Questionnaire/Questionnaire"));
const Question = React.lazy(() => import("@/pages/Question/Question"));
const Interactive = React.lazy(() => import("@/pages/Interactive/Interactive"));
const Robots = React.lazy(() => import("@/pages/Robots/Robots"));
const Reports = React.lazy(() => import("@/pages/Reports/Reports"));
const Users = React.lazy(() => import("@/pages/Users/Users"));
const Roles = React.lazy(() => import("@/pages/Roles/Roles"));
const CallingLine = React.lazy(() => import("@/pages/CallingLine/CallingLine"));
const NotFound = React.lazy(() => import("@/pages/NotFound/NotFound"));

export const AppRoutes = () => {
	const [showNotificationLang, setShowNotificationLang] = useState<boolean>(false); // показ уведомлений статуса загрузки словаря для перевода текста
	const [promiseGetTranslate, setPromiseGetTranslate] = useState<Promise<any> | null>(null); // промис для отмены запроса получения перевода

	const dispatch = useAppDispatch();
	const langStatus = useAppSelector(selectTranslateStatus); // store - статус загрузки словаря для перевода текста
	const langData = useAppSelector(selectTranslate); // store - словарь для перевода текста
	const language = useAppSelector(selectTranslateLanguage); // store - язык
	const authData = useAppSelector(selectAuthData); // store - данные авторизации
	const tokenAuth = useAppSelector(selectTokenAuth); // store - токены авторизации
	const accessRightsList = useAppSelector(selectAccessRights); // store - список всех прав доступа
	const currentUserInfo = useAppSelector(selectCurrentUserInfo); // store - инфо текущего пользователя
	const wallpaper = useAppSelector(selectWallpaper); // store - обои

	const navigate = useNavigate(); // hook для навигации
	const location = useLocation(); // hook для определения адреса
	const [cookies] = useCookies([
		REFRESH_TOKEN,
		QAS_FILTER_CATEGORY,
		QAS_FILTER_QUESTION,
		QAS_FILTER_ANSWER,
		QAS_FILTER_ANSWER_TYPE,
		QAS_FILTER_COMPLETENESS,
		QAS_FILTER_START_DATE,
		QAS_FILTER_END_DATE,
	]); // hook для работы с cookie
	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	useEffect(() => {
		const promise = dispatch(getTranslate({})); // получаем словарь для перевода текста
		setPromiseGetTranslate(promise);
		dispatch(getRandomWallpaper()); // получаем обои
		// если нет refresh токена - перенаправляем на страницу авторизации, иначе получаем заново
		if (!cookies.refreshToken) navigate(LOGIN, { state: location.pathname });
		else dispatch(refreshToken());

		// пишем из cookie в store QAS фильтрацию
		dispatch(
			setFiltersByQas({
				category: cookies.qasFilterCategory ? cookies.qasFilterCategory : undefined,
				question: cookies.qasFilterQuestion,
				answer: cookies.qasFilterAnswer,
				answerType: cookies.qasFilterAnswerType,
				completeness: cookies.qasFilterCompleteness,
				startDate: cookies.qasFilterStartDate,
				endDate: cookies.qasFilterEndDate,
			})
		);
	}, []);

	// следим за статусом авторизации
	useEffect(() => {
		// если ошибка - перенаправляем на страницу авторизации
		if (authData.status === RequestStatus.FAILED) navigate(LOGIN);
	}, [authData.status]);

	// следим за токеном авторизации
	useEffect(() => {
		// если есть
		if (tokenAuth.access && tokenAuth.refresh) {
			dispatch(getAccessRightsList()); // получаем список всех прав доступа
			dispatch(getCurrentUserInfo()); // получаем данные текущего пользователя
		} else {
			currentUserInfo.id && dispatch(clearCurrentUserInfo()); // иначе очищаем, если есть id пользователя
		}
	}, [tokenAuth]);

	// следим за получением прав доступа и в случаем ошибок - переводим на главную страницу
	useEffect(() => {
		if (accessRightsList.status === RequestStatus.FAILED || currentUserInfo.status === RequestStatus.FAILED) navigate("/");
		// когда получены общие права и права пользователя
		if (accessRightsList.data && currentUserInfo.id) {
			isAccess(USER.VERSION) && dispatch(getVersion()); // получаем версию системы
			isAccess(DATASET.LIST) && dispatch(getDatasetsList()); // получаем список наборов данных
			if (promiseGetTranslate && currentUserInfo.language && currentUserInfo.language !== language) {
				// @ts-expect-error аборт промиса
				promiseGetTranslate?.abort();
				const promise = dispatch(getTranslate({ language: currentUserInfo.language })); // получаем словарь для перевода текста
				setPromiseGetTranslate(promise);
			}
		}
	}, [accessRightsList, currentUserInfo]);

	// следим за статусом и данными перевода и в случае ошибок - включаем уведомление
	useEffect(() => {
		if (typeof langData === "string" || langStatus === RequestStatus.FAILED) setShowNotificationLang(true);
	}, [langStatus, langData]);

	// если еще нет данных перевода или в статусе загрузки - показываем блокирующее окно загрузки
	if ((langData === null && langStatus !== RequestStatus.FAILED) || langStatus === RequestStatus.LOADING) return <ScreenLock title="Загрузка языка" />;

	// если идет получение прав доступа - показываем блокирующее окно загрузки
	if (accessRightsList.status === RequestStatus.LOADING || currentUserInfo.status === RequestStatus.LOADING)
		return <ScreenLock title={translate("title_obtainingAccessRights")} />;

	// если идет получение обоев - показываем блокирующее окно загрузки
	if (wallpaper.status === RequestStatus.LOADING) return <ScreenLock title={translate("title_loadingImages")} />;

	return (
		<Suspense fallback={<ScreenLock title={translate("title_loading")} />}>
			<Routes>
				<Route path="/" element={<Home />} />
				<Route path={LOGIN} element={<Login />} />

				<Route path={SMC} element={<Classifier serviceType="smc" />} />
				<Route path={SMC_CORPUS} element={<Corpus serviceType="smc" />} />
				<Route path={SMC_MARKS} element={<Marks serviceType="smc" />} />

				<Route path={SEE} element={<Entities serviceType="see" />} />
				<Route path={SEE_CORPUS} element={<Corpus serviceType="see" />} />

				<Route path={SPR} element={<Queue serviceType="spr" />} />
				<Route path={SPR_TRANSCRIPT} element={<Transcript />} />

				<Route path={SBS} element={<Biometry />} />

				<Route path={TTS} element={<Synthesis serviceType="tts" />} />

				<Route path={QAS} element={<Questionnaire />} />
				<Route path={QAS_QUESTION} element={<Question />} />
				<Route path={`${QAS_QUESTION}/:name`} element={<Question />} />
				<Route path={QAS_INTERACTIVE} element={<Interactive />} />
				<Route path={`${QAS_INTERACTIVE}/:name`} element={<Interactive />} />

				<Route path={SES} element={<Robots />} />
				<Route path={`${SES}/:name`} element={<Robots />} />

				<Route path={CLS} element={<CallingLine />} />

				<Route path={RES} element={<Reports />} />

				<Route path={USERS} element={<Users />} />
				<Route path={USER_ROLES} element={<Roles />} />

				<Route path="*" element={<NotFound />} />
			</Routes>

			{showNotificationLang && <NoticeSingleAction showNotification={showNotificationLang} setShowNotification={setShowNotificationLang} title="Ошибка загрузки языка" />}
		</Suspense>
	);
};
